// SEAT-DRAWER-UX §2.6 (docs/design/2026-10-28-seat-drawer-ux.md): the drawer's Record tab, on a
// seat a digital employee holds. The agent's track record over four weeks, as the DE page's
// Record tab reads it (`digitalEmployees.record`): approved as-is, edited, rejected and
// interventions as tiles (each opens that signal's evidence for the latest week, the record
// tab's own dialog), then outcomes, cost and supervisor time per outcome; under them the seat's
// `de.*` scorecard rows (the panel passes them; a row opens the KPI modal). Nothing to add. An
// empty record says how it fills, with no number (no sample data). DE-KPIS-LIVE: above them, the
// DE's runs, success, cost, budget and memory (the Runs tile opens the week's runs), and its
// helpers (AG) with their calls' cost.
import { ApiError } from "@companyos/sdk";
import { Button } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { RecordSignal } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { DeHelpers, DeOpsTiles, DeOutcomes, OpsCaption } from "../de/de-ops";
import { EvidenceDialog, formatMinutes, formatRate } from "../de/record-tab";
import { formatMoney } from "../format";
import { deRecordQuery } from "../queries";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { PageArrow } from "./section-modal";

/** The weeks the drawer's record reads. */
export const RECORD_WEEKS = 4;

const TILES: { signal: RecordSignal; label: string }[] = [
  { signal: "approved_as_is", label: "Approved as-is" },
  { signal: "edited", label: "Edited" },
  { signal: "rejected", label: "Rejected" },
  { signal: "intervention", label: "Interventions" },
];

export function SeatRecord({
  api,
  company,
  deId,
  rows,
}: {
  api: Api;
  company: CompanyAccess;
  /** The DE's membership id. */
  deId: string;
  /** The seat's record rows, drawn by the panel (KPI rows anatomy). */
  rows: ReactNode;
}) {
  const record = useQuery(deRecordQuery(api, company.id, deId, RECORD_WEEKS));
  const [open, setOpen] = useState<{ week: string; signal: RecordSignal } | null>(null);
  const link = (
    <Link
      to="/c/$slug/agents/de/$id"
      params={{ slug: company.slug, id: deId }}
      search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), tab: "record" as const })}
      className="self-start text-xs text-primary-text hover:underline"
      data-page-link=""
      data-slot="open-record"
    >
      Open the Record
      <PageArrow />
    </Link>
  );
  if (record.isPending) return <Loading label="Loading the record" rows={2} />;
  if (record.isError)
    return record.error instanceof ApiError && record.error.status === 403 ? (
      <p className="text-xs text-faint">The record is shown to the DE's managers.</p>
    ) : (
      <ErrorNotice what="The record" error={record.error} />
    );
  const r = record.data;
  // DE-KPIS-LIVE: its runs, cost, budget and memory first, then its helpers (AG), with or
  // without reviewed work yet.
  const ops = (
    <>
      {r.outcomes.length > 0 ? (
        <div className="flex flex-col gap-0.5" data-slot="seat-outcomes">
          <OpsCaption
            label="Outcomes"
            info="The outcome KPIs of its role on this seat. 'Not tracked yet': only the DE can report it, and it has not."
          />
          <DeOutcomes record={r} compact />
        </div>
      ) : null}
      <DeOpsTiles record={r} compact onOpenRuns={(week) => setOpen({ week, signal: "run" })} />
      <div className="flex flex-col gap-0.5" data-slot="seat-helpers">
        <OpsCaption
          label="Helpers"
          info="The agents (AG) this DE leads or uses, with what their model calls cost in these weeks."
        />
        <DeHelpers helpers={r.helpers} currency={r.currency} compact />
      </div>
    </>
  );
  if (!r.has_data)
    return (
      <div className="flex flex-col gap-1.5" data-slot="record-empty">
        {r.budget || r.helpers.length > 0 || r.outcomes.length > 0 ? ops : null}
        <p className="text-xs text-faint">
          No reviewed work yet. The record fills from approvals, completed tasks and interventions.
        </p>
        {rows}
        {link}
      </div>
    );
  const t = r.total;
  const latest = r.weeks.at(-1)?.week ?? null;
  const value: Record<string, string> = {
    approved_as_is: formatRate(t.approved_as_is_rate),
    edited: formatRate(t.edited_rate),
    rejected: formatRate(t.rejected_rate),
    intervention: String(t.interventions),
  };
  return (
    <div className="flex flex-col gap-2" data-slot="seat-record">
      {ops}
      <div className="grid grid-cols-4 gap-1.5" data-slot="record-tiles">
        {TILES.map((tile) => (
          <Button
            key={tile.signal}
            type="button"
            variant="outline"
            disabled={!latest}
            onClick={() => latest && setOpen({ week: latest, signal: tile.signal })}
            className="h-auto min-w-0 flex-col items-start gap-0.5 px-2 py-1.5 text-left font-normal"
            data-tile={tile.signal}
            title={latest ? `${tile.label}: the evidence for ${latest}` : tile.label}
          >
            <span className="truncate text-[11px] text-faint">{tile.label}</span>
            <span className="text-sm font-semibold tabular-nums">{value[tile.signal]}</span>
          </Button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground tabular-nums" data-slot="record-per-outcome">
        Outcomes {t.outcomes} · Cost / outcome{" "}
        {t.cost_per_outcome_minor === null
          ? "—"
          : formatMoney(t.cost_per_outcome_minor, r.currency)}{" "}
        · Supervisor {formatMinutes(t.supervisor_minutes_per_outcome)} / outcome
      </p>
      {rows}
      {link}
      {open ? (
        <EvidenceDialog
          api={api}
          company={company}
          id={deId}
          week={open.week}
          signal={open.signal}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
